import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  getCategoryColorClass,
  getCategoryLabelKey,
  getTypeLabelKey,
  LEGAL_CATEGORIES,
  LEGAL_COUNTRIES,
  LEGAL_TYPES,
} from "../../../../models/legalDomainConstants";
import dayjs from "dayjs";
import {
  Calendar,
  ClipboardList,
  Eye,
  FileText,
  Filter,
  MapPin,
  Plus,
  Search,
  Tag,
  Trash2,
  User,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import { LegalDocument } from "../../../../models/legalDocument";
import { PaginatedResponse } from "../../../../models/pagination";
import { AxiosHttpClient } from "../../../../services/AxiosHttpService";
import { useAuth } from "../../../Reusable/AuthProvider";
import Pagination from "../../../Reusable/Pagination";
import { AddLegalDocument } from "./AddLegalDocument";
import DeleteLegalDocument from "./DeleteLegalDocument";
import { UploadJobsPanel } from "./UploadJobsPanel";

export default function LegalDocuments() {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState<"keywords" | "semantic">(
    "keywords"
  );
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState("relevance");
  const [legalDocuments, setLegalDocuments] = useState<LegalDocument[]>([]);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [popupCertifyActive, setPopupCertifyActive] = useState(false);
  const [popupDeleteActive, setPopupDeleteActive] = useState("");
  const [uploadJobsPanelOpen, setUploadJobsPanelOpen] = useState(false);

  const pageSize = 10;

  const { user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.roles?.some((role: string) => role.includes("ADMIN"));

  // Fonction pour récupérer les documents depuis l'API avec filtres et pagination
  const getLegalDocuments = useCallback(
    async (resetPage = false) => {
      setIsLoading(true);

      const page = resetPage ? 0 : currentPage - 1; // Spring commence à 0
      if (resetPage) {
        setCurrentPage(1);
      }

      const params = new URLSearchParams({
        page: page.toString(),
        size: pageSize.toString(),
        sort: sortBy,
      });

      // Ajouter les filtres de recherche
      if (searchQuery.trim()) {
        params.append("search", searchQuery);
        params.append("searchType", searchType);
      }

      // Ajouter les filtres
      if (selectedCountries.length > 0) {
        selectedCountries.forEach((countryId) =>
          params.append("countries", countryId)
        );
      }
      if (selectedCategories.length > 0) {
        selectedCategories.forEach((category) =>
          params.append("categories", category)
        );
      }
      if (selectedTypes.length > 0) {
        selectedTypes.forEach((type) => params.append("types", type));
      }

      try {
        const response = await AxiosHttpClient.get<
          PaginatedResponse<LegalDocument>
        >(`api/v1/docuveritas/legal-documents?${params.toString()}`);

        setLegalDocuments(response.content);
        setTotalDocuments(response.totalElements);
      } catch (error) {
        console.error("Erreur lors de la récupération des documents:", error);
        setLegalDocuments([]);
        setTotalDocuments(0);
      } finally {
        setIsLoading(false);
      }
    },
    [
      currentPage,
      pageSize,
      sortBy,
      searchQuery,
      searchType,
      selectedCountries,
      selectedCategories,
      selectedTypes,
    ]
  );

  // Charger les documents quand les filtres changent (avec debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        // Si on est déjà sur la page 1, on fait directement l'appel
        getLegalDocuments(false);
      } else {
        // Sinon on retourne à la page 1, ce qui déclenchera l'autre useEffect
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    searchQuery,
    searchType,
    selectedCountries,
    selectedCategories,
    selectedTypes,
    sortBy,
  ]);

  // Charger les documents quand la page change
  useEffect(() => {
    getLegalDocuments(false);
  }, [currentPage]);

  // Valeurs fixes prédéfinies pour les filtres
  const countries = LEGAL_COUNTRIES;
  const categories = LEGAL_CATEGORIES;
  const types = LEGAL_TYPES;

  const totalPages = Math.ceil(totalDocuments / pageSize);

  // Fonction helper pour obtenir le nom d'un pays par son ID
  const getCountryNameById = (countryId: string) => {
    const country = LEGAL_COUNTRIES.find((c) => c.id === countryId);
    return country ? t(country.labelKey) : countryId;
  };

  const handleFilterChange = (
    type: "countries" | "categories" | "types",
    value: string
  ) => {
    const setters = {
      countries: setSelectedCountries,
      categories: setSelectedCategories,
      types: setSelectedTypes,
    };

    const getters = {
      countries: selectedCountries,
      categories: selectedCategories,
      types: selectedTypes,
    };

    const currentValues = getters[type];
    const setter = setters[type];

    if (currentValues.includes(value)) {
      setter(currentValues.filter((item) => item !== value));
    } else {
      setter([...currentValues, value]);
    }
  };

  const clearAllFilters = () => {
    setSelectedCountries([]);
    setSelectedCategories([]);
    setSelectedTypes([]);
    setSearchQuery("");
    setCurrentPage(1);
  };

  return (
    <div className="min-h-full w-full">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Documents Juridiques
              </h1>
              <p className="text-muted-foreground">
                Recherchez et consultez notre base de documents juridiques
              </p>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setUploadJobsPanelOpen(true)}
                  className="flex items-center gap-2"
                >
                  <ClipboardList className="w-4 h-4" />
                  Suivi des imports
                </Button>
                <Button
                  onClick={() => setPopupCertifyActive(true)}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter un document
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-[300px_1fr] gap-8">
          {/* Sidebar - Filtres */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filtres
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAllFilters}
                  className="w-full bg-transparent"
                >
                  Effacer tous les filtres
                </Button>

                <Accordion type="single" collapsible defaultValue="search">
                  <AccordionItem value="search">
                    <AccordionTrigger>Type de recherche</AccordionTrigger>
                    <AccordionContent>
                      <Tabs
                        value={searchType}
                        onValueChange={(value) =>
                          setSearchType(value as "keywords" | "semantic")
                        }
                      >
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="keywords">Mots-clés</TabsTrigger>
                          <TabsTrigger value="semantic">Sémantique</TabsTrigger>
                        </TabsList>
                      </Tabs>
                      <p className="text-xs text-muted-foreground mt-2">
                        {searchType === "keywords"
                          ? "Utilisez 'et', 'ou', 'sauf' pour combiner les termes"
                          : "Recherche basée sur le sens et le contexte"}
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="countries">
                    <AccordionTrigger>
                      Pays ({selectedCountries.length})
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2">
                        {countries.map((country) => (
                          <div
                            key={country.id}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={`country-${country.id}`}
                              checked={selectedCountries.includes(country.id)}
                              onCheckedChange={() =>
                                handleFilterChange("countries", country.id)
                              }
                            />
                            <Label
                              htmlFor={`country-${country.id}`}
                              className="text-sm"
                            >
                              {t(country.labelKey)}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="categories">
                    <AccordionTrigger>
                      Catégories ({selectedCategories.length})
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2">
                        {categories.map((category) => (
                          <div
                            key={category.value}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={`category-${category.value}`}
                              checked={selectedCategories.includes(category.value)}
                              onCheckedChange={() =>
                                handleFilterChange("categories", category.value)
                              }
                            />
                            <Label
                              htmlFor={`category-${category.value}`}
                              className="text-sm"
                            >
                              {t(category.labelKey)}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="types">
                    <AccordionTrigger>
                      Types ({selectedTypes.length})
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2">
                        {types.map((type) => (
                          <div
                            key={type.value}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={`type-${type.value}`}
                              checked={selectedTypes.includes(type.value)}
                              onCheckedChange={() =>
                                handleFilterChange("types", type.value)
                              }
                            />
                            <Label htmlFor={`type-${type.value}`} className="text-sm">
                              {t(type.labelKey)}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </CardContent>
            </Card>
          </div>

          {/* Contenu principal */}
          <div className="space-y-6">
            {/* Barre de recherche et tri */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input
                      placeholder={
                        searchType === "keywords"
                          ? "Rechercher... (ex: contrat et propriété sauf commercial)"
                          : "Rechercher par sens et contexte..."
                      }
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-full md:w-48">
                      <SelectValue placeholder="Trier par" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="relevance">Pertinence</SelectItem>
                      <SelectItem value="date">Date de publication</SelectItem>
                      <SelectItem value="name">Nom du document</SelectItem>
                      <SelectItem value="author">Auteur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Résultats */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {isLoading
                  ? "Chargement..."
                  : `${totalDocuments} document${
                      totalDocuments !== 1 ? "s" : ""
                    } trouvé${totalDocuments !== 1 ? "s" : ""}`}
                {totalDocuments > 0 && (
                  <span className="ml-2 text-muted-foreground/60">
                    (Page {currentPage} sur {totalPages})
                  </span>
                )}
              </p>
            </div>

            {/* Liste des documents */}
            <div className="space-y-4">
              {isLoading ? (
                <div className="flex justify-center items-center p-8">
                  <div className="text-muted-foreground">
                    Chargement des documents...
                  </div>
                </div>
              ) : (
                legalDocuments.map((document) => (
                  <Card
                    key={document.id}
                    className="hover:shadow-md transition-shadow"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-3">
                          <div className="flex items-start gap-3">
                            <FileText className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-foreground mb-1">
                                {document.title}
                              </h3>
                              <p className="text-muted-foreground text-sm mb-2">
                                {document.description}
                              </p>
                              {document.snippet && (
                                <p
                                  className="text-sm text-muted-foreground italic border-l-2 border-blue-300 pl-3 mt-1 mb-2"
                                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(document.snippet) }}
                                />
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-muted-foreground" />
                              <span className="text-muted-foreground">
                                {document.authors.join(", ")}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-muted-foreground" />
                              <span className="text-muted-foreground">
                                {document.jurisdiction?.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Tag className="w-4 h-4 text-muted-foreground" />
                              <span className="text-muted-foreground">
                                {t(getCategoryLabelKey(document.category))}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-muted-foreground" />
                              <span className="text-muted-foreground">
                                {document.createdAt
                                  ? dayjs(document.createdAt).format(
                                      "YYYY-MM-DD"
                                    )
                                  : ""}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{t(getTypeLabelKey(document.type))}</Badge>
                            {document.tags.slice(0, 3).map((tag) => (
                              <Badge
                                key={tag}
                                variant="outline"
                                className="text-xs"
                              >
                                {tag}
                              </Badge>
                            ))}
                            {document.tags.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{document.tags.length - 3}
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 ml-4">
                          <Button
                            size="sm"
                            className="w-8 h-8 p-0"
                            onClick={() => {
                              window.open(
                                `/feature/legalDocuments/pdf-viewer/${document.id}`,
                                "_blank"
                              );
                            }}
                            title="Visualiser"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => setPopupDeleteActive(document.id)}
                              className="w-8 h-8 p-0"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}

              {!isLoading && legalDocuments.length === 0 && (
                <Card>
                  <CardContent className="p-12 text-center">
                    <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      Aucun document trouvé
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Essayez de modifier vos critères de recherche ou vos
                      filtres.
                    </p>
                    <Button onClick={clearAllFilters} variant="outline">
                      Effacer tous les filtres
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Pagination */}
            {!isLoading && totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalItems={totalPages * 10} // Approximation basée sur 10 items par page
                itemsPerPage={10}
                onPageChange={setCurrentPage}
                itemName="documents"
                className="mt-8 justify-center"
              />
            )}
          </div>
        </div>
      </div>

      {/* Popups d'administration */}
      {popupCertifyActive && (
        <AddLegalDocument
          popupActive={popupCertifyActive}
          setPopupActive={setPopupCertifyActive}
        />
      )}

      <UploadJobsPanel
        open={uploadJobsPanelOpen}
        onOpenChange={setUploadJobsPanelOpen}
        getLegalDocuments={() => getLegalDocuments(true)}
      />

      {popupDeleteActive && (
        <DeleteLegalDocument
          popupActive={popupDeleteActive}
          setPopupActive={setPopupDeleteActive}
          getLegalDocuments={() => getLegalDocuments(true)}
        />
      )}
    </div>
  );
}
